"use client";
import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { BackgroundRippleEffect } from "@/components/ui/background-ripple-effect";
import { ContainerTextFlip } from "@/components/ui/container-text-flip";
import { ProcessDemo } from "@/components/ui/process-demo";
import { AnimatedTooltip } from "@/components/ui/animated-tooltip";
import { useAuth } from "@/lib/store";
import { useLogin } from "@/components/providers/LoginProvider";
import { useRouter } from "next/navigation";
import { IconFlare } from "@tabler/icons-react";
const people = [
  {
    id: 1,
    name: "Tina patil",
    designation: "QMUL",
    image:
      "boys/girl1.png",
  },
  {
    id: 2,
    name: "Rohit sharma",
    designation: "Oxford Brookes",
    image:
      "boys/boy1.png",
  },
  {
    id: 3,
    name: "Jiya shetty",
    designation: "Buffalo University",
    image:
      "boys/girl2.png",
  },
  {
    id: 4,
    name: "Rupindar Singh",
    designation: "University of Calgary",
    image:
      "boys/boy2.png",
  },
  {
    id: 5,
    name: "Priya kapoor",
    designation: "Dalhousie University",
    image:
      "boys/girl3.png",
  },
  {
    id: 6,
    name: "Rakesh Shah",
    designation: "York University",
    image:
      "boys/boy3.png",
  },
];

export default function HeroSection() {
  const { isAuthenticated } = useAuth();
  const { openLogin, switchToSignup } = useLogin();
  const router = useRouter();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      router.push('/scorer');
    } else {
      switchToSignup();
    }
  };

  const handleAiCheck = () => {
    if (isAuthenticated) {
      router.push('/ai-detection');
    } else {
      openLogin();
    }
  };

  return (
    <div className="relative w-full overflow-hidden">
      {/* Full screen background ripple effect */}
      <div className="absolute inset-0 w-full h-full sm:translate-0 translate-y-[-150px]">
        <BackgroundRippleEffect rows={15} cols={40} cellSize={56} />
      </div>

      {/* Content Grid: Left content, Right empty */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-16 lg:py-20 min-h-[600px]">
          {/* Left Side - Content */}
          <div className="space-y-4 text-left">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Powered by Advanced AI Technology
            </motion.div>

            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold"
            >
              <span className="text-foreground">
                Assignment
              </span>
              <br />
              <ContainerTextFlip
                words={["Proofreading", "Scoring", "Feedback"]}
                className="text-4xl md:text-5xl lg:text-6xl font-bold mt-5"
              />            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl text-muted-foreground max-w-lg leading-relaxed"
            >
              Get high quality feedback on your assignments. Trained and tested on 5000+ assignments for accurate marks prediction.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 pt-4"
            >
              {/* Primary Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  size="lg"
                  onClick={handleGetStarted}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all duration-300 group cursor-pointer"
                >
                  {"Start Grading"}
                </Button>
              </motion.div>

              {/* Secondary Button - AI Check */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleAiCheck}
                  className="border-2 border-border/40 bg-background/80 backdrop-blur-sm px-8 py-6 text-lg font-semibold rounded-full hover:bg-accent/50 hover:border-primary/60 hover:shadow-[0_0_30px_rgba(139,92,246,0.6)] transition-all duration-300 group cursor-pointer"
                >
                  <IconFlare className="mr-3 h-7 w-7 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                  Check AI
                </Button>
              </motion.div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="grid grid-cols-3 gap-8 pt-6 max-w-md"
            >
              {[
                { number: "3K+", label: "Assignments Graded" },
                { number: "90%", label: "Success Rate" },
                { number: "100%", label: "Reliable" },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 + index * 0.1 }}
                  className="text-left"
                >
                  <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                    {stat.number}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
            <div className="flex flex-row items-center mb-10 w-full">
              <AnimatedTooltip items={people} />
            </div>
          </div>

          {/* Right Side - Process Demo */}
          <div className="flex items-center justify-center lg:justify-end">
            <ProcessDemo />
          </div>
        </div>
      </div>
    </div>
  );
}
